import React from 'react'
import type { FunMedia } from 'components/fun'
import { getFallbackMedia, PRE_API_MEDIA, RANDOM_MEDIA_POOL, shuffleMedia } from '../constants/media'
import type { DisplayMode } from '../types'
import usePrefersReducedMotion from './usePrefersReducedMotion'

type UseFunMediaPlaylistOptions = {
  open: boolean
  hasFirstResponse: boolean
  funModeEnabled: boolean
  displayMode: DisplayMode
}

type UseFunMediaPlaylistResult = {
  currentMedia: FunMedia | null
  showGifPanel: boolean
  showGamePanel: boolean
}

const useFunMediaPlaylist = ({
  open,
  hasFirstResponse,
  funModeEnabled,
  displayMode,
}: UseFunMediaPlaylistOptions): UseFunMediaPlaylistResult => {
  const prefersReducedMotion = usePrefersReducedMotion()
  const [playlist, setPlaylist] = React.useState<FunMedia[]>([])
  const [index, setIndex] = React.useState(0)

  React.useEffect(() => {
    if (!open || !funModeEnabled) {
      setPlaylist([])
      setIndex(0)
      return
    }

    const base = hasFirstResponse ? RANDOM_MEDIA_POOL : PRE_API_MEDIA
    setPlaylist(shuffleMedia(base))
    setIndex(0)
  }, [open, hasFirstResponse, funModeEnabled])

  React.useEffect(() => {
    if (!open) return undefined
    if (!funModeEnabled) return undefined
    if (prefersReducedMotion) return undefined
    if (playlist.length <= 1) return undefined
    if (displayMode !== 'gifs') return undefined

    const intervalMs = hasFirstResponse ? 12000 : 8000

    const id = window.setInterval(() => {
      setIndex((prev) => {
        const next = prev + 1
        if (next < playlist.length) return next
        setPlaylist((prevPlaylist) => shuffleMedia(prevPlaylist))
        return 0
      })
    }, intervalMs)

    return () => window.clearInterval(id)
  }, [
    open,
    funModeEnabled,
    prefersReducedMotion,
    playlist.length,
    hasFirstResponse,
    displayMode,
  ])

  const fallback = getFallbackMedia(hasFirstResponse)
  const mediaCandidate = playlist[index] ?? fallback
  const currentMedia = funModeEnabled ? mediaCandidate : null

  return {
    currentMedia,
    showGifPanel: Boolean(funModeEnabled && displayMode === 'gifs' && currentMedia),
    showGamePanel: funModeEnabled && displayMode === 'game',
  }
}

export default useFunMediaPlaylist
